import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeZh from '@angular/common/locales/zh';
import {
	APP_INITIALIZER,
	type ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	isDevMode,
	LOCALE_ID,
	provideEnvironmentInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
// Guards are now functions, no need to import classes
import { AuthInterceptor } from '@interceptors/http-interceptor';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import Aura from '@primeuix/themes/aura';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@services/auth/auth.service';
import { LanguageDetectionService } from '@services/domain/language-detection.service';

import { provideLottieOptions } from 'ngx-lottie';
import primelocaleEn from 'primelocale/en.json';
import primelocaleEs from 'primelocale/es.json';
import primelocaleZh from 'primelocale/zh-CN.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

// Angular locale data for every supported language (LOCALE_ID below). Templates format numbers, amounts
// and dates with the appCurrency/appNumber/appDate pipes, which follow the active language
registerLocaleData(localeEs);
registerLocaleData(localeEnGb);
registerLocaleData(localeZh);

import { routes } from './app.routes';
import { BRANDING } from './core/constants/branding.constants';
import { DEFAULT_LANG, SUPPORTED_LANGS, type SupportedLang } from './core/constants/languages';
import { localeForLang } from './core/utils/locale';
import { TranslocoHttpLoader } from './transloco-loader';

/** PrimeNG's own texts (calendar days and months, filter menus, empty messages) per app language. */
const PRIMENG_TRANSLATIONS: Record<SupportedLang, typeof primelocaleEs.es> = {
	es: primelocaleEs.es,
	en: primelocaleEn.en,
	zh: primelocaleZh['zh-CN'],
};

export const appConfig: ApplicationConfig = {
	providers: [
		// Locale of the language the visit starts in (Angular's LOCALE_ID cannot change after bootstrap)
		{
			provide: LOCALE_ID,
			useFactory: () => localeForLang(inject(LanguageDetectionService).resolveLanguage(BRANDING.defaultLanguage)),
		},
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes, withComponentInputBinding()),
		importProvidersFrom(FormsModule, ReactiveFormsModule),
		provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
		provideAnimations(),

		// Sentry Error Handler
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler(),
		},
		provideLottieOptions({
			player: () => import('lottie-web'),
		}),
		providePrimeNG({
			theme: {
				preset: Aura,
				options: {
					prefix: 'p',
					darkModeSelector: '[data-theme="dark"]',
					cssLayer: false,
				},
			},
			ripple: true,
			translation: primelocaleEs.es,
			// Mobile like an app: dropdowns (select, multiselect, autocomplete) become a
			// bottom sheet with a mask. The slide up/down animation lives in _sheets.scss.
			overlayOptions: {
				responsive: { breakpoint: '844px', direction: 'bottom' },
			},
		}),

		// PrimeNG texts follow the active language instead of staying in Spanish
		provideEnvironmentInitializer(() => {
			const primeng = inject(PrimeNG);
			inject(TranslocoService).langChanges$.subscribe((lang) => {
				const translation = PRIMENG_TRANSLATIONS[lang as SupportedLang];
				if (translation) primeng.setTranslation(translation);
			});
		}),

		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		MessageService,
		AuthService,
		ConfirmationService,
		DynamicDialogRef,
		DynamicDialogConfig,
		DialogService,

		// Auto-refresh token on app startup (persistent login)
		{
			provide: APP_INITIALIZER,
			useFactory: (authService: AuthService) => () => authService.initAuth().toPromise(),
			deps: [AuthService],
			multi: true,
		},

		// Activate and load the visitor's language before the first screen: many components build tabs,
		// columns and labels with the synchronous translate() in their constructor, and on a hard reload
		// the JSON wasn't there yet, so they showed the raw keys
		{
			provide: APP_INITIALIZER,
			useFactory: (transloco: TranslocoService, detection: LanguageDetectionService) => () => {
				const lang = detection.resolveLanguage(BRANDING.defaultLanguage);
				transloco.setActiveLang(lang);
				return firstValueFrom(transloco.load(lang)).catch(() => undefined);
			},
			deps: [TranslocoService, LanguageDetectionService],
			multi: true,
		},

		provideClientHydration(),
		provideTransloco({
			config: {
				availableLangs: [...SUPPORTED_LANGS],
				defaultLang: DEFAULT_LANG,
				fallbackLang: 'en',
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
				missingHandler: {
					// Missing keys show up in the dev console only
					logMissingKey: isDevMode(),
					useFallbackTranslation: true,
					allowEmpty: false,
				},
			},
			loader: TranslocoHttpLoader,
		}),

		// Service Worker for PWA - Production only
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
};
